import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Snackbar,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DesktopWindowsIcon from '@material-ui/icons/DesktopWindows';
import PhoneIphoneIcon from '@material-ui/icons/PhoneIphone';
import { TemplateType, TemplateConfig, getTemplateById, PlacementSelector, PreviewLinkData } from './types';
import { TemplatePreview, PreviewLink } from './TemplatePreview';
import { savePreviewModeConfig, generateBookmarkletCode } from './cloudService';
import BookmarkletHowTo from './BookmarkletHowTo';
import { useSession } from '../../../../utility/services/customHooks/auth';
import styles from './styles/templateConfigurator.module.css';

const useStyles = makeStyles((theme) => ({
  previewSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
    borderRadius: 8,
    border: '1px solid #86efac',
  },
  previewSectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    color: '#1e3a5f',
    fontWeight: 600,
    fontSize: '0.9rem',
  },
  previewActions: {
    display: 'flex',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  pushButton: {
    background: '#1e3a5f',
    color: 'white',
    '&:hover': {
      background: '#2d4a6f',
    },
  },
  bookmarkletButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    background: 'linear-gradient(135deg, #3ecf8e 0%, #22c55e 100%)',
    color: 'white',
    padding: '8px 16px',
    borderRadius: 6,
    textDecoration: 'none',
    fontWeight: 500,
    fontSize: '0.85rem',
    cursor: 'grab',
    boxShadow: '0 2px 4px rgba(62, 207, 142, 0.3)',
    transition: 'all 0.2s',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 8px rgba(62, 207, 142, 0.4)',
    },
  },
  bookmarkletButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    pointerEvents: 'none',
    boxShadow: 'none',
    '&:hover': {
      transform: 'none',
      boxShadow: 'none',
    },
  },
  successChip: {
    background: '#dcfce7',
    color: '#166534',
    fontWeight: 500,
  },
}));

interface PreviewModalProps {
  open: boolean;
  onClose: () => void;
  pageUrl: string;
  pageTitle: string;
  moduleId?: string;
  moduleName?: string;
  template: TemplateType;
  config: TemplateConfig;
  links: PreviewLink[];
  placementSelector?: PlacementSelector | null;
}

export const PreviewModal: React.FC<PreviewModalProps> = ({
  open,
  onClose,
  pageUrl,
  pageTitle,
  moduleId,
  moduleName,
  template,
  config,
  links,
  placementSelector,
}) => {
  const classes = useStyles();
  const { session } = useSession();
  const schema = session?.selected_schema || '';
  const normalizedPageUrl = pageUrl?.trim();
  const normalizedPageTitle = pageTitle?.trim();
  const showPageTitle = Boolean(normalizedPageTitle && normalizedPageUrl && normalizedPageTitle !== normalizedPageUrl);
  const [deviceView, setDeviceView] = useState<'desktop' | 'mobile'>('desktop');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pushingToCloud, setPushingToCloud] = useState(false);
  const [pushSuccess, setPushSuccess] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const bookmarkletCode = generateBookmarkletCode(schema, moduleId);
  const templateDef = getTemplateById(template);
  const bookmarkletRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (open) {
      setLoading(true);
      setError(null);
      setPushSuccess(false);
      const timer = setTimeout(() => {
        setLoading(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [open, pageUrl]);

  // Update bookmarklet href via ref to avoid React javascript: URL warning
  useEffect(() => {
    if (bookmarkletRef.current) {
      bookmarkletRef.current.setAttribute('href', pushSuccess ? bookmarkletCode : '#');
    }
  }, [bookmarkletCode, pushSuccess]);

  const handlePushToCloud = async () => {
    if (!moduleId) {
      setSnackbarMessage('Missing module ID. Please refresh and try again.');
      setSnackbarOpen(true);
      return;
    }

    setPushingToCloud(true);
    try {
      // Convert PreviewLink to PreviewLinkData format
      const linkData: PreviewLinkData[] = links.map((link) => ({
        url: link.url,
        anchorText: link.anchorText || link.title || link.url,
        title: link.title,
        description: link.description,
        image: link.image,
        category: link.category,
        readingTime: link.readingTime,
        publishDate: link.publishDate,
        author: link.author,
        excerpt: link.excerpt,
        videoThumbnail: link.videoThumbnail,
        videoDuration: link.videoDuration,
        channelName: link.channelName,
        viewCount: link.viewCount,
        updatedDate: link.updatedDate,
      }));

      // Extract placement from prop or from config
      let placement = placementSelector;
      if (!placement && config.placementSelector) {
        placement = {
          selector: config.placementSelector as string,
          position: (config.placementPosition as 'before' | 'after' | 'inside_start' | 'inside_end') || 'after',
          savedAt: Date.now(),
        };
      }

      const result = await savePreviewModeConfig(
        template,
        templateDef?.name || template,
        config,
        linkData,
        pageUrl,
        pageTitle,
        placement || undefined,
        moduleId,
        moduleName
      );

      if (result.success) {
        setPushSuccess(true);
        setSnackbarMessage('Configuration pushed to cloud! Use the bookmarklet to preview.');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage(result.error || 'Failed to push to cloud');
        setSnackbarOpen(true);
      }
    } catch (err) {
      setSnackbarMessage('Failed to push configuration');
      setSnackbarOpen(true);
    } finally {
      setPushingToCloud(false);
    }
  };

  const handleOpenInNewTab = () => {
    window.open(pageUrl, '_blank');
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth className={styles.previewModal}>
        <DialogTitle className={styles.previewModalTitle}>
          <Box className={styles.titleContent}>
            <Box>
              <Typography variant="h6">Preview on Page</Typography>
              {showPageTitle ? (
                <Typography variant="body2" color="textSecondary" noWrap>
                  {pageTitle}
                </Typography>
              ) : null}
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <Box className={styles.previewToolbar}>
          <Box className={styles.deviceToggle}>
            <button
              type="button"
              className={`${styles.deviceButton} ${deviceView === 'desktop' ? styles.deviceButtonActive : ''}`}
              onClick={() => setDeviceView('desktop')}
            >
              <DesktopWindowsIcon style={{ fontSize: 16 }} />
              Desktop
            </button>
            <button
              type="button"
              className={`${styles.deviceButton} ${deviceView === 'mobile' ? styles.deviceButtonActive : ''}`}
              onClick={() => setDeviceView('mobile')}
            >
              <PhoneIphoneIcon style={{ fontSize: 16 }} />
              Mobile
            </button>
          </Box>
          <Button variant="outlined" size="small" startIcon={<OpenInNewIcon />} onClick={handleOpenInNewTab}>
            Open Page
          </Button>
        </Box>

        <DialogContent className={styles.previewModalContent}>
          {loading ? (
            <Box className={styles.loadingContainer}>
              <CircularProgress />
              <Typography variant="body2" color="textSecondary">
                Loading preview...
              </Typography>
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <>
              <Box className={`${styles.pagePreviewContainer} ${deviceView === 'mobile' ? styles.mobileView : ''}`}>
                <Box className={styles.mockPage}>
                  <Box className={styles.mockHeader}>
                    <Typography variant="body2" className={styles.mockUrl}>
                      {pageUrl}
                    </Typography>
                  </Box>
                  <Box className={styles.mockContent}>
                    {showPageTitle ? (
                      <Typography variant="h5" gutterBottom>
                        {pageTitle}
                      </Typography>
                    ) : null}
                    <Typography variant="body2" color="textSecondary" paragraph>
                      Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut
                      labore et dolore magna aliqua.
                    </Typography>

                    <Box className={styles.templateInsertPoint}>
                      <Typography variant="caption" className={styles.insertLabel}>
                        ↓ Template Preview ↓
                      </Typography>
                      <TemplatePreview template={template} config={config} links={links} />
                    </Box>

                    <Typography variant="body2" color="textSecondary" paragraph>
                      Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo
                      consequat.
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Preview on Your Website Section */}
              <Box className={classes.previewSection}>
                <Typography className={classes.previewSectionTitle}>🌐 Preview on Your Website</Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12, fontSize: '0.8rem' }}>
                  Save your config to cloud, then use the bookmarklet on your site.
                </Typography>

                <Box className={classes.previewActions}>
                  <Button
                    variant="contained"
                    color="primary"
                    className="primary-button font-500-ft12-lh16 text-white text-title-case radius-0 no-box-shadow"
                    startIcon={pushingToCloud ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                    onClick={handlePushToCloud}
                    disabled={pushingToCloud}
                    size="small"
                    style={{ textTransform: 'none' }}
                  >
                    {pushingToCloud ? 'Pushing...' : 'Push to Cloud'}
                  </Button>

                  {pushSuccess && <Chip label="✓ Ready" size="small" className={classes.successChip} />}

                  <a
                    ref={bookmarkletRef}
                    href="#"
                    className={`${classes.bookmarkletButton} ${!pushSuccess ? classes.bookmarkletButtonDisabled : ''}`}
                    onClick={(e) => e.preventDefault()}
                    draggable={pushSuccess}
                    aria-disabled={!pushSuccess}
                    tabIndex={pushSuccess ? 0 : -1}
                    title="Drag this up to your bookmarks bar"
                  >
                    Quattr Preview
                  </a>
                </Box>

                <BookmarkletHowTo bookmarkletCode={bookmarkletCode} enabled={pushSuccess} />
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions className={`${styles.previewModalActions} buttons-inline`}>
          <Button
            onClick={onClose}
            color="primary"
            className="internal-linking-advance-filters-cancel-btn"
            style={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </>
  );
};

export default PreviewModal;
